import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { NavLink, Navigate, Outlet } from "react-router-dom";
import { useCurrentUser } from "../../hooks/use-auth.js";
import { useLanguage } from "../../hooks/use-language.js";
import { isAuthUnknown } from "../../lib/auth-state.js";
import { Button } from "../ui/button.js";

function navClass({ isActive }: { isActive: boolean }) {
  return [
    "pb-2 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-ring rounded-sm",
    isActive ? "border-b-2 border-primary text-foreground" : "text-muted-fg hover:text-foreground",
  ].join(" ");
}

export function AccountLayout() {
  const intl = useIntl();
  const { language } = useLanguage();
  const { data: user, isLoading, isPaused, error } = useCurrentUser();

  // `page.error.body` says "we have been notified" - so somebody has to be.
  // The page this copy comes from calls Sentry itself; reusing the string
  // without reusing that call would have made the sentence a lie, and nothing
  // else captures a failed /users/me (main.tsx registers no QueryCache
  // onError). Above the early returns because hooks cannot sit below them.
  //
  // `isPaused` belongs in this test for the same reason it does in header.tsx
  // and admin.tsx: TanStack's default networkMode is "online", so a query
  // started while the browser is offline reports isLoading FALSE with data
  // undefined and WILL still answer once connectivity returns. That is
  // waiting, not failing - without the term, going offline on this page would
  // claim the account could not be loaded and report a pause to Sentry as an
  // error.
  const authUnknown = isAuthUnknown(user) && !isLoading && !isPaused;
  useEffect(() => {
    if (authUnknown && error) Sentry.captureException(error);
  }, [authUnknown, error]);

  // `isLoading` is `isPending && isFetching` (query-core's queryObserver), so
  // it already means "no data yet" - a cached user makes it false. `isPaused`
  // carries no such narrowing: a signed-in visitor coming back to /account
  // with a STALE cached user and no connection sits at fetchStatus "paused"
  // with `data` still there. Blanking on that would take the nav away from
  // someone we can render perfectly well from cache, with no error text and
  // no way back. So the pause only means "wait" while there is nothing to
  // show yet - the same narrowing isLoading has.
  if (isLoading || (isPaused && user === undefined)) return null;

  // Exactly one thing proves "not signed in": a clean 401, which
  // useCurrentUser folds to null. Everything else - a 500, a dropped
  // connection, a response that fails schema validation because api and web
  // are on different versions - leaves the question unanswered, and `data`
  // stays undefined.
  //
  // Until 2026-09-02 neither branch below caught that, so the component fell
  // through and rendered its nav around an empty Outlet: a page with a menu
  // and nothing on it, and no way out. Redirecting to sign-in instead would
  // be the other mistake - it asserts the visitor is logged out, which is
  // precisely what we could not establish. So say what is true (we could not
  // load the account) and offer the one action that helps.
  if (user === null) return <Navigate to={`/${language}/auth/sign-in`} replace />;
  if (!user) {
    return (
      <section className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-xl font-semibold">{intl.formatMessage({ id: "page.error.title" })}</h2>
        <p className="text-muted-fg">{intl.formatMessage({ id: "page.error.body" })}</p>
        <Button variant="primary" size="md" onClick={() => window.location.reload()}>
          {intl.formatMessage({ id: "page.error.cta" })}
        </Button>
      </section>
    );
  }

  const base = `/${language}/account`;

  return (
    <div className="flex flex-col gap-6">
      <nav
        className="flex gap-6 border-b border-border"
        aria-label={intl.formatMessage({ id: "account.title" })}
      >
        <NavLink to={base} end className={navClass}>
          {intl.formatMessage({ id: "account.nav.settings" })}
        </NavLink>
        <NavLink to={`${base}/redaction`} className={navClass}>
          {intl.formatMessage({ id: "account.nav.redaction" })}
        </NavLink>
        <NavLink to={`${base}/api-keys`} className={navClass}>
          {intl.formatMessage({ id: "account.nav.api_keys" })}
        </NavLink>
        <NavLink to={`${base}/usage`} className={navClass}>
          {intl.formatMessage({ id: "account.nav.usage" })}
        </NavLink>
      </nav>
      <Outlet />
    </div>
  );
}
